import React, { useState, useEffect, ReactElement } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { PoweredBy } from './PoweredBy';
import { ENV } from '@/constants';
import Logo from './Logo';
import clsx from 'clsx';
import HomePageHeader from '../DiscoverCalls/HomePageHeader';

export const Layout: React.ElementType = ({ children }): ReactElement<any> => {
  const { query, pathname, asPath } = useRouter();
  const hasTrailingId = (() => {
    const path = asPath.split('?')[0].split('#')[0].replace(/\/+$/, '');
    return /^\/discovercalls\/[^/]+$/.test(path);
  })();

  const campaignShortId = query.campaignId as string;
  const utmTags = {
    utm_source: campaignShortId ? 'discover-campaign' : 'discover-homepage',
    utm_medium: 'discover-public',
    utm_content: 'header',
    ...(campaignShortId ? { utm_campaign: campaignShortId } : {}),
  };

  const [returnTo, setReturnTo] = useState<string>('/');
  useEffect(() => {
    if (typeof window !== 'undefined' && pathname.startsWith('/discovercalls')) {
      setReturnTo(`${window.location.protocol}//${window.location.host}/discovercalls/${campaignShortId}`);
    }
  }, [pathname]);

  const loginUrl = `${ENV.NEXT_PUBLIC_COMPANIES_APP_URL}/login?userType=academic&returnTo=${returnTo}`;
  const registerUrl = `${
    ENV.NEXT_PUBLIC_COMPANIES_APP_URL
  }/register/academic?returnTo=${returnTo}&${new URLSearchParams(utmTags).toString()}`;

  return (
    <>
      {hasTrailingId ? (
        <section className="flex items-center justify-between bg-white border-b py-2 px-6 sm:py-4 w-full border-outline-neutral-light md:fixed md:top-0 md:z-100">
          <>
            <Link
              href="/"
              aria-label={`${ENV.NEXT_PUBLIC_ORGANISATION_NAME} home`}
              className="flex items-center justify-center"
            >
              <Logo title={`${ENV.NEXT_PUBLIC_ORGANISATION_NAME} Logo`} />
            </Link>
          </>

          <div className="flex items-center gap-4 text-body-md leading-4 ">
            <Link href={registerUrl} className="hover:underline text-content-secondary">
              Create a free account
            </Link>
            <Link href={loginUrl} className="hover:underline text-content-secondary">
              Log in
            </Link>
          </div>
        </section>
      ) : (
        <HomePageHeader loginUrl={loginUrl} registerUrl={registerUrl} />
      )}
      <div
        className={clsx({
          'bg-general-neutral-lighter min-h-screen': hasTrailingId,
          'md:pt-[70px]': !hasTrailingId,
        })}
      >
        <div
          className={clsx('mx-auto px-[15px] md:px-[30px]', {
            'w-full pb-5': hasTrailingId,
            'max-w-[1500px]': !hasTrailingId,
          })}
        >
          {children}
        </div>
      </div>
      {!hasTrailingId && (
        <footer className="w-full flex flex-col items-center pt-10 pb-4 bg-general-neutral-light mt-15">
          <div className="mb-8">
            <PoweredBy
              organisationName={ENV.NEXT_PUBLIC_ORGANISATION_NAME}
              strapline={true}
              linkUrl="https://in-part.com"
            />
          </div>
          <div className="border-t border-outline-neutral-light pt-4 px-4 ">
            <p className="p-0 m-0 text-body-md font-normal leading-[1rem] text-content-tertiary text-center">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" className="text-content-tertiary underline">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" className="text-content-tertiary underline">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </footer>
      )}
    </>
  );
};
